<script setup lang="ts">
// MemberTable — account members + pending invites as one table (contract 22 §10). Rebuilt from
// the hand-rolled `<table>` at `UsersPermissions.vue:17-117` onto 02's `DataTable` + `Pagination`.
//
// ⚠ THIS IS A PUBLIC API. It is exported on 22's barrel because module 29's `PackageLimitModal.vue`
// embeds the whole members table today (`<UsersPermissions for-package-modal />`, `:47, :120` — the
// undeclared consumer analysis §7 R2 found). The alternative — 29 composing `useMembers()` and
// re-implementing five columns, the "You" marker, the status tones and the pager — is how the
// second copy of this table gets written. So the props below are designed AS a contract, not as
// whatever the pane happened to need:
//
//   • `rows` is `MemberListRow[]`, the shared read model — the caller chooses `useMembers().rows`,
//     `.members` or its own subset. The table never fetches, so it cannot double-fetch inside a
//     modal that already has the list.
//   • `state` is 02's `UiAsyncState`, so an embedder that is still loading gets skeletons instead
//     of an empty table. The pane leaves it at `ready` because `SettingsPaneShell` owns its five
//     states one level up — two different jobs, not a duplicated one.
//   • `actions` is an opt-IN allowlist and defaults to `[]`. That is exactly v3's `forPackageModal`
//     re-skin (`:96` hides edit, `:103/:111` keep remove), expressed as data instead of a boolean
//     prop that re-skins someone else's component. Role editing joins the union with ORP-18889's
//     invite modal; nothing here changes when it does.
//   • the OWNER row never renders an action, whatever `actions` says (`:90-93`) — the rule is the
//     table's, not the caller's, so no embedder can hand a user a Remove button for the owner.
//   • the ownership-transfer trigger is NOT here. v3 put it inside `TransferOwnershipModal` and
//     therefore rendered a whole modal per row (`:91`); v4 lifts it to the pane.
//   • `currentUserEmail` marks "You" and is display only — authority is never re-derived from an
//     email on the client (audit ORP-18613).
//   • `pageSize` pages CLIENT-side, because `GET /accounts/{a}/users` is unpaginated (BE-22-04).
//     `DataTable`'s own `pageSize`/`load-more` is infinite scroll, a different mechanism, so the
//     pager composes beside it rather than through it.
//
// The embedder owns 22's message bundle; routes register it, while a modal in 29 must do so itself.
import { computed, reactive, watch } from 'vue'

import {
  isPendingInviteRow,
  type AccountMemberRow,
  type MemberListRow,
  type MemberRole,
  type PendingInviteRow,
} from '../../models/member.types'
import { memberRoleLabelKey, memberScopeLabelKey } from '../../models/memberCatalog'
import type { MemberRowAction } from '../../models/memberTable.types'
import { foldEmail } from '../../service/memberList'

import { useI18n } from '@/shared/i18n'
import {
  Badge,
  Button,
  DataTable,
  Icon,
  Pagination,
  type PaginationChange,
  type IconName,
  type TableColumn,
  type UiAsyncState,
  type UiTone,
} from '@/shared/ui'

/**
 * A type alias, not an interface: `DataTable`'s `Row extends Record<string, unknown>` constraint
 * needs the implicit index signature only aliases carry. `source` is the domain row travelling
 * with its display projection — it is not a column, so it is never rendered or stringified.
 */
type MemberTableRow = {
  key: string
  email: string
  role: string
  scope: string
  status: string
  source: MemberListRow
}

const props = withDefaults(
  defineProps<{
    rows: readonly MemberListRow[]
    state?: UiAsyncState
    /** The signed-in user's address — renders the "You" marker. Display only. */
    currentUserEmail?: string | undefined
    actions?: readonly MemberRowAction[]
    /** Rows per page for the client-side pager. `0` renders every row and hides the pager. */
    pageSize?: number
    /** A write is in flight — every row action is disabled until it settles. */
    busy?: boolean
  }>(),
  { state: 'ready', actions: () => [], pageSize: 25, busy: false },
)

const emit = defineEmits<{
  /** An accepted member — carries a `UserId`, addresses `/users/{id}/remove`. */
  remove: [row: AccountMemberRow]
  /** A pending invite — carries an `InviteId`, addresses `/permission_invites/{id}/delete`. */
  revoke: [row: PendingInviteRow]
  resend: [row: PendingInviteRow]
  /** ORP-18889 — open the grant editor. Either row kind; `MemberInviteModal` narrows for the body. */
  edit: [row: MemberListRow]
  retry: []
}>()

const { t, te } = useI18n()

// Shared Tailwind bundles, and the unknown -> MemberTableRow cast the cell slots all need —
// pulled out so the template doesn't repeat either across every cell.
const ATTRIBUTE_BADGE_CLASS =
  'min-h-6 border border-border bg-surface px-sm py-2xs text-xs leading-[0.875rem] text-fg'
const ROLE_BADGE_CLASS = `member-table__attribute-badge member-table__role-badge ${ATTRIBUTE_BADGE_CLASS}`
const SCOPE_BADGE_CLASS = `member-table__attribute-badge member-table__scope-badge ${ATTRIBUTE_BADGE_CLASS}`
const ICON_ACTION_CLASS =
  'member-table__icon-action border-border !text-muted focus-visible:outline-[length:var(--ui-ring-width)] focus-visible:outline-solid focus-visible:outline-ring focus-visible:outline-offset-2'
const STATE_CLASS = 'member-table__state m-0 p-lg text-center font-sans text-sm text-muted'

function asRow(row: unknown): MemberTableRow {
  return row as MemberTableRow
}

function removeLabel(row: MemberListRow): string {
  return actionLabel(isPendingInviteRow(row) ? 'revoke' : 'remove', row)
}

const showActions = computed(() => props.actions.length > 0)

const columns = computed<TableColumn<MemberTableRow>[]>(() => {
  const base: TableColumn<MemberTableRow>[] = [
    { field: 'email', header: t('workspaceSettings.permissions.table.email'), width: 230 },
    { field: 'role', header: t('workspaceSettings.permissions.table.role'), width: 125 },
    { field: 'scope', header: t('workspaceSettings.permissions.table.scope'), width: 145 },
    { field: 'status', header: t('workspaceSettings.permissions.table.status'), width: 165 },
  ]
  if (!showActions.value) return base
  return [
    ...base,
    {
      field: 'source',
      header: t('workspaceSettings.permissions.table.actions'),
      width: 90,
      align: 'center',
    },
  ]
})

/** `status` is the SERVER's word (`pending`/`failed`/…). Keyed when we know it, echoed when we do
 *  not — an unrecognised server value is data, not a hardcoded string. */
function statusLabel(row: MemberListRow): string {
  if (!isPendingInviteRow(row)) return t('workspaceSettings.permissions.status.active')
  if (row.isExpired) return t('workspaceSettings.permissions.status.expired')
  const key = `workspaceSettings.permissions.status.${row.status}`
  return row.status === ''
    ? t('workspaceSettings.permissions.status.pending')
    : te(key)
      ? t(key)
      : row.status
}

/** v3's three tones (`:61-67`), minus its "no status ⇒ green" inference: the row union says so. */
function statusTone(row: MemberListRow): UiTone {
  if (!isPendingInviteRow(row)) return 'success'
  if (row.isExpired || row.status === 'failed' || row.status === 'expired') return 'danger'
  return 'warning'
}

function roleLabel(row: MemberTableRow): string {
  return t('workspaceSettings.permissions.table.roleBadgeLabel', { role: row.role })
}

function roleIcon(role: MemberRole): IconName {
  switch (role) {
    case 'owner':
      return 'user'
    case 'admin':
      return 'gear'
    case 'editor':
      return 'square-pen'
    case 'viewer_plus':
      return 'users'
    case 'viewer':
      return 'eye-off'
  }
}

function scopeLabel(row: MemberTableRow): string {
  return t('workspaceSettings.permissions.table.scopeBadgeLabel', { scope: row.scope })
}

function statusAriaLabel(row: MemberTableRow): string {
  return t('workspaceSettings.permissions.table.statusBadgeLabel', { status: row.status })
}

function statusIcon(row: MemberListRow): IconName {
  if (!isPendingInviteRow(row)) return 'check'
  return row.isExpired ? 'x' : 'clock'
}

function actionLabel(action: 'edit' | 'remove' | 'revoke' | 'resend', row: MemberListRow): string {
  return t(`workspaceSettings.permissions.${action}.ariaLabel`, { email: row.email })
}

const tableRows = computed<MemberTableRow[]>(() =>
  props.rows.map((row, index) => ({
    key: isPendingInviteRow(row) ? `invite:${row.inviteId}` : `member:${row.userId}:${index}`,
    email: row.email,
    role: t(memberRoleLabelKey(row.role)),
    scope: t(memberScopeLabelKey(row.scope)),
    status: statusLabel(row),
    source: row,
  })),
)

const page = reactive({ first: 0, rows: props.pageSize > 0 ? props.pageSize : 0 })

// A changed list invalidates the offset — page 4 of a 3-row list renders nothing.
watch(
  () => props.rows.length,
  () => (page.first = 0),
)

const pagerVisible = computed(
  () => props.pageSize > 0 && page.rows > 0 && tableRows.value.length > page.rows,
)

const visibleRows = computed(() => {
  if (props.pageSize <= 0 || page.rows <= 0) return tableRows.value
  return tableRows.value.slice(page.first, page.first + page.rows)
})

function onPaginate(change: PaginationChange): void {
  page.first = change.first
  page.rows = change.rows
}

function isYou(email: string): boolean {
  return (
    props.currentUserEmail !== undefined && foldEmail(email) === foldEmail(props.currentUserEmail)
  )
}

function isOwnerRow(row: MemberListRow): boolean {
  return !isPendingInviteRow(row) && row.isOwner
}

/** Expired ⇒ Resend (`:81-86`), and only ever on an invite. */
function canResend(row: MemberListRow): row is PendingInviteRow {
  return props.actions.includes('resend') && isPendingInviteRow(row) && row.isExpired
}

function canRemove(row: MemberListRow): boolean {
  return props.actions.includes('remove') && !isOwnerRow(row)
}

/** v3 hid Edit on the owner row and in the package modal (`:96`); both stay true here. */
function canEdit(row: MemberListRow): boolean {
  return props.actions.includes('edit') && !isOwnerRow(row)
}

function onRemove(row: MemberListRow): void {
  if (isPendingInviteRow(row)) emit('revoke', row)
  else emit('remove', row)
}
</script>

<template>
  <div class="member-table flex w-full min-w-0 max-w-full flex-col gap-md">
    <DataTable
      :rows="visibleRows"
      :columns="columns"
      :state="props.state"
      v-bind="{ ariaLabel: t('workspaceSettings.permissions.table.ariaLabel') }"
      :row-key="(row: MemberTableRow) => row.key"
      :interactive-rows="false"
      @retry="emit('retry')"
    >
      <template #cell-email="{ row }">
        <span class="member-table__email inline-flex items-center gap-2xs">
          {{ asRow(row).email }}
          <Badge v-if="isYou(asRow(row).email)" tone="info" size="sm">
            {{ t('workspaceSettings.permissions.table.you') }}
          </Badge>
        </span>
      </template>

      <template #cell-role="{ row }">
        <Badge
          tone="neutral"
          size="lg"
          shape="rounded"
          :class="ROLE_BADGE_CLASS"
          data-testid="member-role-badge"
          :aria-label="roleLabel(asRow(row))"
        >
          <Icon :name="roleIcon(asRow(row).source.role)" :size="14" aria-hidden="true" />
          {{ asRow(row).role }}
        </Badge>
      </template>

      <template #cell-scope="{ row }">
        <Badge
          tone="neutral"
          size="lg"
          shape="rounded"
          :class="SCOPE_BADGE_CLASS"
          data-testid="member-scope-badge"
          :aria-label="scopeLabel(asRow(row))"
        >
          {{ asRow(row).scope }}
        </Badge>
      </template>

      <template #cell-status="{ row }">
        <div class="member-table__status inline-flex items-center gap-xs">
          <Badge
            :tone="statusTone(asRow(row).source)"
            size="lg"
            shape="rounded"
            class="member-table__status-badge min-h-6 border border-current px-sm py-2xs text-xs leading-[0.875rem]"
            data-testid="member-status-badge"
            :aria-label="statusAriaLabel(asRow(row))"
          >
            <Icon :name="statusIcon(asRow(row).source)" :size="13" aria-hidden="true" />
            {{ asRow(row).status }}
          </Badge>

          <button
            v-if="canResend(asRow(row).source)"
            type="button"
            class="member-table__resend cursor-pointer border-0 bg-transparent p-0 text-xs font-semibold text-info font-[inherit] disabled:cursor-default disabled:opacity-50 focus-visible:outline-[length:var(--ui-ring-width)] focus-visible:outline-solid focus-visible:outline-ring focus-visible:outline-offset-2"
            :disabled="props.busy"
            :aria-label="actionLabel('resend', asRow(row).source)"
            :title="actionLabel('resend', asRow(row).source)"
            data-testid="member-resend"
            @click="emit('resend', asRow(row).source as PendingInviteRow)"
          >
            {{ t('workspaceSettings.permissions.resend.action') }}
          </button>
        </div>
      </template>

      <template #cell-source="{ row }">
        <div class="member-table__actions inline-flex items-center justify-center gap-xs">
          <Button
            v-if="canEdit(asRow(row).source)"
            variant="ghost"
            size="sm"
            square
            :disabled="props.busy"
            :aria-label="actionLabel('edit', asRow(row).source)"
            :title="actionLabel('edit', asRow(row).source)"
            :class="ICON_ACTION_CLASS"
            data-testid="member-edit"
            @click="emit('edit', asRow(row).source)"
          >
            <Icon name="square-pen" :size="16" aria-hidden="true" />
          </Button>

          <Button
            v-if="canRemove(asRow(row).source)"
            variant="ghost"
            size="sm"
            square
            :disabled="props.busy"
            :aria-label="removeLabel(asRow(row).source)"
            :title="removeLabel(asRow(row).source)"
            :class="ICON_ACTION_CLASS"
            data-testid="member-remove"
            @click="onRemove(asRow(row).source)"
          >
            <Icon name="x" :size="16" aria-hidden="true" />
          </Button>

          <span
            v-if="isOwnerRow(asRow(row).source)"
            class="member-table__locked text-muted"
            data-testid="member-owner-locked"
            role="img"
            :aria-label="t('workspaceSettings.permissions.table.ownerLocked')"
          >
            {{ t('workspaceSettings.permissions.table.noActions') }}
          </span>
        </div>
      </template>

      <template #empty>
        <p :class="STATE_CLASS">{{ t('workspaceSettings.permissions.empty') }}</p>
      </template>

      <template #error>
        <p :class="STATE_CLASS">{{ t('workspaceSettings.permissions.loadFailed') }}</p>
      </template>
    </DataTable>

    <Pagination
      v-if="pagerVisible"
      :total="tableRows.length"
      :first="page.first"
      :rows="page.rows"
      :rows-label="t('workspaceSettings.permissions.table.rowsPerPage')"
      @change="onPaginate"
    />
  </div>
</template>

<style scoped src="./MemberTable.css"></style>
